/**
 * EventPage — /event/:id
 *
 * - Loads event detail (getEventDetail)
 * - Subscribes to WebSocket odds stream (useOddsStream)
 * - Renders: LiveScoreTracker, MarketCard list, AIPredictionPanel, BetSlip
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LiveScoreTracker from '../components/LiveScoreTracker';
import MarketCard from '../components/MarketCard';
import AIPredictionPanel from '../components/AIPredictionPanel';
import BetSlip from '../components/BetSlip';
import LoadingSpinner from '../components/LoadingSpinner';
import { getEventDetail, type EventDetail } from '../services/api';
import { useOddsStream } from '../hooks/useOddsStream';

function ShimmerEventHeader() {
  return (
    <div className="space-y-4">
      <div className="shimmer h-32 w-full rounded-card" />
      <div className="shimmer h-5 w-48 rounded" />
    </div>
  );
}

function ShimmerMarket() {
  return (
    <div className="rounded-card border border-border bg-surface p-4 shadow-card space-y-3">
      <div className="shimmer h-4 w-32 rounded" />
      <div className="flex gap-2">
        <div className="shimmer h-12 flex-1 rounded-button" />
        <div className="shimmer h-12 flex-1 rounded-button" />
        <div className="shimmer h-12 flex-1 rounded-button" />
      </div>
    </div>
  );
}

export default function EventPage() {
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const liveOdds = useOddsStream(id);

  const loadEvent = () => {
    if (!id) return;
    setLoading(true);
    setError(null);

    getEventDetail(id)
      .then((res) => {
        setEvent(res.data);
      })
      .catch(() => {
        setError('Failed to load event. Please try again.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const eventName =
    event ? `${event.home_team} vs ${event.away_team}` : 'Event';

  return (
    <div className="flex min-h-screen flex-col">
      <Header />

      <main className="flex-1 mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Back link */}
        {event && (
          <Link
            to={`/sport/${event.sport_slug}`}
            className="mb-4 inline-flex items-center gap-1 text-sm text-text-muted hover:text-primary transition-colors"
          >
            Back to {event.league}
          </Link>
        )}

        {loading ? (
          <div className="space-y-6">
            <ShimmerEventHeader />
            <LoadingSpinner height="h-6" className="w-32" />
            <ShimmerMarket />
            <ShimmerMarket />
          </div>
        ) : error ? (
          <div className="rounded-card border border-error/20 bg-error/5 px-6 py-8 text-center">
            <p className="text-error font-medium">{error}</p>
            <button
              onClick={loadEvent}
              className="mt-4 rounded-button bg-primary px-6 py-2 text-sm font-semibold text-white shadow-btn hover:bg-primary-dark transition-colors"
            >
              Retry
            </button>
          </div>
        ) : event ? (
          <div className="lg:grid lg:grid-cols-[1fr_340px] lg:gap-8">
            {/* Left column: event info + markets */}
            <div className="space-y-6">
              {/* Score tracker */}
              <LiveScoreTracker event={event} />

              {/* AI Prediction Panel */}
              <AIPredictionPanel
                eventId={event.id}
                homeTeam={event.home_team}
                awayTeam={event.away_team}
              />

              {/* New-to-betting explainer */}
              <details className="rounded-card border border-border bg-surface-2 px-4 py-3 text-sm">
                <summary className="cursor-pointer font-semibold text-text-primary">
                  New to betting? How it works
                </summary>
                <div className="mt-2 space-y-1.5 text-text-secondary">
                  <p>1. Pick an outcome below — e.g. who wins the match.</p>
                  <p>
                    2. The number is the <strong>odds</strong>. It multiplies your stake if you win:
                    at odds of 2.00, a R100 bet returns R200 (your R100 back + R100 profit).
                  </p>
                  <p>
                    3. A <strong>★ Favourite</strong> is most likely to win, so it pays less. An{' '}
                    <strong>Underdog</strong> is less likely, so it pays more.
                  </p>
                  <p>4. Tap a pick, enter your stake in the bet slip, and place your bet.</p>
                </div>
              </details>

              {/* Markets */}
              {event.markets.length === 0 ? (
                <div className="rounded-card border border-border bg-surface px-6 py-10 text-center">
                  <p className="text-text-muted">No betting options available for this match yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <h2 className="font-heading text-xl font-bold text-text-primary">Place a bet</h2>
                  {event.markets.map((market) => (
                    <MarketCard
                      key={market.id}
                      market={market}
                      eventId={event.id}
                      eventName={eventName}
                      homeTeam={event.home_team}
                      awayTeam={event.away_team}
                      liveOdds={liveOdds}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Right column: BetSlip (desktop sidebar) */}
            <div className="hidden lg:block">
              <div className="sticky top-20">
                <BetSlip variant="sidebar" />
              </div>
            </div>
          </div>
        ) : null}
      </main>

      <Footer />

      {/* Mobile BetSlip — fixed bottom drawer */}
      <div className="lg:hidden">
        <BetSlip variant="drawer" />
      </div>
    </div>
  );
}
